import { ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import { parseChat, parseChatList, type ChatSummary } from './aiAgentContract'

/**
 * useChats — the chat-thread LIST composable for the in-app AI assistant.
 *
 * Owns listing / creating / deleting chat threads against the ai-agent-service
 * (`config.api.agentHost`). Owner-or-Full scoping is enforced SERVER-SIDE
 * (the backend filters by the caller's Cognito `sub`, or returns all threads
 * for a `Full` user) — the frontend simply renders whatever the API returns.
 *
 * Network access lives ONLY here (never in the .vue), honoring the
 * `no_httpservice_in_vue` qa rule and CLAUDE.md rules 3/5. Requests use an
 * ABSOLUTE URL to the agent host; httpService passes absolute URLs through
 * verbatim and attaches `Authorization: Bearer <jwt>` at the transport layer.
 * The JWT is NEVER an argument here — it is request-scoped and transport-injected.
 *
 * `skipErrorDisplay` so this composable owns the error surface (`error.value`)
 * instead of also firing the global toast; PT messages are mapped here and the
 * raw error text is kept in the structured log only (never the JWT).
 */
export function useChats() {
  const { t } = useI18n()
  const chats: Ref<ChatSummary[]> = ref([])
  const archivedChats: Ref<ChatSummary[]> = ref([])
  const loading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)

  /**
   * The ai-agent-service answers a missing/null billingFranquiaId with a NAMED
   * code (HTTP 400 `{ error: 'billing_franquia_required' }`) rather than the
   * generic `invalid_request` it uses for a bad title, so the caller can tell
   * "you must pick who pays" apart from "your input was malformed". The code is
   * read off the parsed error body (`HttpError.data`), never off the message
   * text, which is not a contract.
   */
  function ptError(err: unknown): string {
    const httpErr = err as HttpError
    const code = (httpErr?.data as { error?: string } | undefined)?.error
    if (code === 'billing_franquia_required') {
      return t('Selecione a franquia que vai pagar')
    }
    return httpErr?.status === 401
      ? t('Sua sessão expirou. Faça login novamente.')
      : t('Erro ao carregar as conversas. Tente novamente.')
  }

  async function listChats(): Promise<ChatSummary[]> {
    loading.value = true
    error.value = null

    try {
      const raw: unknown = await httpService.get(`${config.api.agentHost}/chats`, {
        skipErrorDisplay: true,
        logContext: 'useChats',
      })
      chats.value = parseChatList(raw)
      log('useChats', 'listChats', 'Conversas carregadas', { count: chats.value.length })
      return chats.value
    } catch (err) {
      error.value = ptError(err)
      const message = err instanceof Error ? err.message : String(err)
      log('useChats', 'listChats', 'Falha ao carregar conversas', {
        message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * createChat — POST /chats. `billingFranquiaId` is REQUIRED (S7-R01): every
   * chat must name the franchise whose credit wallet pays for its turns, and
   * the backend now refuses a missing/null value with 400
   * `billing_franquia_required`. Callers that do not yet know the franchise must
   * ASK the user first (AssistantSidebar opens the franchise modal) rather than
   * posting and hoping — a request without it can only fail.
   *
   * `title` stays optional; the backend names the thread when it is absent.
   */
  async function createChat(billingFranquiaId: number, title?: string): Promise<ChatSummary> {
    loading.value = true
    error.value = null

    const body: Record<string, unknown> = { billingFranquiaId }
    if (title !== undefined) body.title = title

    try {
      const raw: unknown = await httpService.post(`${config.api.agentHost}/chats`, body, {
        skipErrorDisplay: true,
        logContext: 'useChats',
      })
      const chat = parseChat(raw)
      chats.value = [chat, ...chats.value]
      log('useChats', 'createChat', 'Conversa criada', { id: chat.id })
      return chat
    } catch (err) {
      error.value = ptError(err)
      const message = err instanceof Error ? err.message : String(err)
      log('useChats', 'createChat', 'Falha ao criar conversa', {
        message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  async function renameChat(id: number, title: string): Promise<ChatSummary> {
    loading.value = true
    error.value = null

    try {
      const raw: unknown = await httpService.patch(
        `${config.api.agentHost}/chats/${id}`,
        { title },
        { skipErrorDisplay: true, logContext: 'useChats' },
      )
      const chat = parseChat(raw)
      chats.value = chats.value.map((c) => (c.id === id ? chat : c))
      log('useChats', 'renameChat', 'Conversa renomeada', { id })
      return chat
    } catch (err) {
      error.value = ptError(err)
      const message = err instanceof Error ? err.message : String(err)
      log('useChats', 'renameChat', 'Falha ao renomear conversa', {
        message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  async function deleteChat(id: number): Promise<void> {
    loading.value = true
    error.value = null

    try {
      await httpService.delete(`${config.api.agentHost}/chats/${id}`, {
        skipErrorDisplay: true,
        logContext: 'useChats',
      })
      chats.value = chats.value.filter((chat) => chat.id !== id)
      log('useChats', 'deleteChat', 'Conversa removida', { id })
    } catch (err) {
      error.value = ptError(err)
      const message = err instanceof Error ? err.message : String(err)
      log('useChats', 'deleteChat', 'Falha ao remover conversa', {
        message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  async function archiveChat(id: number): Promise<void> {
    loading.value = true
    error.value = null

    try {
      await httpService.post(`${config.api.agentHost}/chats/${id}/archive`, {}, {
        skipErrorDisplay: true,
        logContext: 'useChats',
      })
      chats.value = chats.value.filter((chat) => chat.id !== id)
      log('useChats', 'archiveChat', 'Conversa arquivada', { id })
    } catch (err) {
      error.value = ptError(err)
      const message = err instanceof Error ? err.message : String(err)
      log('useChats', 'archiveChat', 'Falha ao arquivar conversa', {
        message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  async function listArchivedChats(): Promise<ChatSummary[]> {
    loading.value = true
    error.value = null

    try {
      const raw: unknown = await httpService.get(
        `${config.api.agentHost}/chats?archived=true`,
        { skipErrorDisplay: true, logContext: 'useChats' },
      )
      archivedChats.value = parseChatList(raw)
      log('useChats', 'listArchivedChats', 'Conversas arquivadas carregadas', {
        count: archivedChats.value.length,
      })
      return archivedChats.value
    } catch (err) {
      error.value = ptError(err)
      const message = err instanceof Error ? err.message : String(err)
      log('useChats', 'listArchivedChats', 'Falha ao carregar conversas arquivadas', {
        message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    chats,
    archivedChats,
    loading,
    error,
    listChats,
    createChat,
    renameChat,
    deleteChat,
    archiveChat,
    listArchivedChats,
  }
}
